import { Trans, useLingui } from '@lingui/react/macro'
import { LoadingOverlay, Stack, Tabs } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { AppPage } from '@markku/components'
import { isCompanyType } from '@markku/utils'
import { IconCircleCheck, IconX } from '@tabler/icons-react'
import {
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { SUBSCRIPTION_SALES_ENABLED } from '../../../../config/features'
import { usePermissions } from '../../../../lib/hooks/usePermissions'
import { billingHooks } from '../../../../lib/trpcHooks/billing'

import { propertyHooks } from '../../../../lib/trpcHooks'
import { servicePackageHooks } from '../../../../lib/trpcHooks/servicePackage'
import { subscriptionHooks } from '../../../../lib/trpcHooks/subscription'
import { HomeDetails } from '../../../../modules/maintenance/HomeDetails'
import { PropertyAddressCard } from '../../../../modules/property/components'
import {
  ActiveSubscriptionDetails,
  NoSubscriptionCard,
  PackageListSection,
  ProposalActivationCard,
  SubscriptionOverviewCard,
} from '../../../../modules/subscription/components'

const searchSchema = z.object({
  checkout: z.enum(['success', 'cancelled']).optional(),
  tab: z.enum(['overview', 'package']).optional(),
})

function PropertyDetailPage() {
  const { t } = useLingui()
  const search = Route.useSearch()
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<string | null>(
    search.tab || 'overview',
  )
  const { propertyId } = Route.useParams()
  const { data: property } = propertyHooks.useGetById(Number(propertyId))

  // Business (company / housing company) properties are managed in the company portal,
  // not on this B2C property page — send them there.
  const isBusinessProperty =
    !!property?.customerId && isCompanyType(property.customer?.type)

  useEffect(() => {
    if (isBusinessProperty && property) {
      navigate({
        to: '/company-portal/$companyId/properties/$propertyId',
        params: {
          companyId: String(property.customerId),
          propertyId: String(propertyId),
        },
        replace: true,
      })
    }
  }, [isBusinessProperty, property, propertyId, navigate])

  // Extract floor area from property data
  const existingData =
    property?.data && typeof property.data === 'object' ? property.data : {}
  const floorArea =
    'floorArea' in existingData &&
    existingData.floorArea !== null &&
    existingData.floorArea !== undefined &&
    existingData.floorArea !== ''
      ? Number(existingData.floorArea)
      : null

  // Pass floor area to query - React Query will auto-refetch when it changes
  const { data: packages } = servicePackageHooks.useListActive(floorArea)

  // Handle hash scrolling after property data loads
  useEffect(() => {
    if (location.hash && property) {
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(location.hash)
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [location.hash, property])

  const { mutateAsync: createCheckout, isPending: isCreatingCheckout } =
    billingHooks.useCreateCheckoutSession()
  const { isOwnerOf } = usePermissions()

  const isOwner = property ? isOwnerOf(property.customerId) : false
  const activeSubscription = property?.subscriptions?.find(
    (sub) => sub.status === 'active',
  )
  const proposalSubscription = property?.subscriptions?.find(
    (sub) => sub.status === 'proposal',
  )

  const { data: activeSubscriptionDetails } = subscriptionHooks.useGetById(
    activeSubscription?.id,
  )

  // Without self-service sales the package tab only exists for customers who already subscribe.
  const showPackageTab = SUBSCRIPTION_SALES_ENABLED || !!activeSubscription

  const handleSubscribe = async (servicePackageVersionId: number) => {
    await createCheckout({
      propertyId: Number(propertyId),
      servicePackageVersionId,
    })
  }

  if (isBusinessProperty) {
    return (
      <AppPage title={property?.address?.street || t`Property`} backTarget="/">
        <LoadingOverlay visible />
      </AppPage>
    )
  }

  return (
    <AppPage title={property?.address?.street || t`Property`} backTarget="/">
      <Tabs
        value={showPackageTab ? activeTab : 'overview'}
        onChange={setActiveTab}
        variant="default"
      >
        {showPackageTab && (
          <Tabs.List grow>
            <Tabs.Tab value="overview">
              <Trans>Overview</Trans>
            </Tabs.Tab>
            <Tabs.Tab value="package">
              <Trans>Markku package</Trans>
            </Tabs.Tab>
          </Tabs.List>
        )}

        <Tabs.Panel value="overview" pt="lg">
          <Stack gap="lg">
            {activeSubscription ? (
              <SubscriptionOverviewCard
                subscriptionDetails={activeSubscriptionDetails}
                onManageClick={() => setActiveTab('package')}
              />
            ) : proposalSubscription && property && isOwner ? (
              <ProposalActivationCard
                subscription={proposalSubscription}
                property={property}
              />
            ) : (
              isOwner &&
              SUBSCRIPTION_SALES_ENABLED && (
                <NoSubscriptionCard
                  onGetStartedClick={() => setActiveTab('package')}
                />
              )
            )}
            {property ? (
              <HomeDetails property={property} />
            ) : (
              <LoadingOverlay visible />
            )}
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="package" pt="lg">
          <Stack gap="lg" pb={80}>
            {property && <PropertyAddressCard property={property} />}

            {activeSubscriptionDetails && (
              <ActiveSubscriptionDetails
                subscriptionDetails={activeSubscriptionDetails}
              />
            )}

            {proposalSubscription &&
              property &&
              isOwner &&
              !activeSubscription && (
                <ProposalActivationCard
                  subscription={proposalSubscription}
                  property={property}
                />
              )}

            <PackageListSection
              packages={packages}
              property={property}
              activeSubscription={activeSubscription}
              proposalSubscription={proposalSubscription}
              isCreatingCheckout={isCreatingCheckout}
              onSubscribe={handleSubscribe}
              canSubscribe={isOwner}
            />
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </AppPage>
  )
}

export const Route = createFileRoute('/_app/properties/$propertyId/')({
  validateSearch: searchSchema,
  beforeLoad: ({ search, params }) => {
    // Note: These notification messages use hardcoded English because they run before
    // the i18n context is available in beforeLoad. The LinguiJS setup requires hooks
    // which can only be used inside React components.
    if (search.checkout === 'success') {
      notifications.show({
        title: 'Order successful!',
        message: 'Markku package is now active on your property',
        color: 'green',
        icon: <IconCircleCheck />,
      })
      throw redirect({
        to: '/properties/$propertyId',
        params: { propertyId: params.propertyId },
      })
    } else if (search.checkout === 'cancelled') {
      notifications.show({
        title: 'Order cancelled',
        message: 'You can try again anytime',
        color: 'yellow',
        icon: <IconX />,
      })
      throw redirect({
        to: '/properties/$propertyId',
        params: { propertyId: params.propertyId },
      })
    }
  },
  component: PropertyDetailPage,
})
